import { FunctionComponent, memo, useEffect } from "react";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import TextInputFieldC from "../../Components/TextInput/TextInputFieldC";
import { useAppDispatch, useAppSelector } from "../../Redux/Store/Hooks";
import { addCourses } from "../../Redux/Slices/Courses/AddCoursesSlice";
import { EditCourses } from "../../Redux/Slices/Courses/EditCoursesSlice";
import Select from "../../Components/Select/AutoComplete";
import { AutocompleteOption } from "../../Components/Select/ISelectProps";
import TextAreaInputField from "../../Components/TextAreaInput/TextAreaInputField";
import { ICourseslist } from "./ICourses";
import NumberInputField from "../../Components/NumberInput/NumberInputField";
import MultiSelect from "../../Components/MultiSelect/MultiSelect";
import { Box, Checkbox, FormControlLabel, TextField } from "@mui/material";

interface CoursesFormProps {
  formRef: React.RefObject<HTMLFormElement>;
  initialValues: ICourseslist;
  setInitialValues: React.Dispatch<React.SetStateAction<ICourseslist>>;
}

const CoursesForm: FunctionComponent<CoursesFormProps> = (props) => {
  const dispatch = useAppDispatch();

  const { data: listExams } = useAppSelector((state) => state.ListExamSlice);

  const ExamList = Array.isArray(listExams?.data)
    ? listExams?.data.map((listExams) => ({
      label: listExams.name!,
      value: listExams.id,
    }))
    : [];

  // Define allowed image MIME types
  const allowedMimeTypes = ["image/jpeg", "image/png", "image/gif"];

  // Define the maximum file size (e.g., 5MB)
  const MAX_FILE_SIZE = 1 * 1024 * 1024; // 5 MB in bytes

  const userSchema = z
    .object({
      id: z.number().optional(),
      exam: z.boolean().optional(),
      ai: z.boolean().optional(),
      study_note: z.boolean().optional(),
      video: z.boolean().optional(),
      booklet: z.boolean().optional(),
      image_url: z
        .union([
          z
            .instanceof(File)
            .refine(
              (file) => file && allowedMimeTypes.includes(file.type),
              "Invalid file type. Only JPEG, PNG, and GIF files are allowed."
            )
            .refine(
              (file) => file && file.size <= MAX_FILE_SIZE,
              "File size should not exceed 1MB."
            ),
          z.string().optional(),
          z.null().optional(),
        ])
        .optional(),
      sequence: z.preprocess(
        (val) => (val === "" ? undefined : val),
        z.number().optional().or(z.nan())
      ),

      year_group: z.number({
        required_error: "Please select year group",
        invalid_type_error: "Please select year group",
      }),
      name: z
        .string({ message: "Please enter name" })
        .min(1, { message: "Please enter name" })
        .max(100, { message: "name cannot exceed 100 characters" }),
      description: z.string().optional(),
      cost: z.preprocess(
        (val) => (val === "" ? undefined : val),
        z.number().optional().or(z.nan())
      ),

      duration: z.preprocess(
        (val) => (val === "" ? undefined : val),
        z.number().optional().or(z.nan())
      ),
      status: z.boolean({
        message: "Please select the status",
      }),
    });
  const methods = useForm<ICourseslist>({
    defaultValues: {
      ...props.initialValues,
      status: props.initialValues.id ? props.initialValues.status : true,
    },
    resolver: zodResolver(userSchema),
    mode: "onChange",
  });

  const onSubmit: SubmitHandler<ICourseslist> = (val) => {
    if (val.id) {
      dispatch(EditCourses(val));
    } else {
      dispatch(addCourses(val));
    }
  };
  const { data: listYearlevel_maps } = useAppSelector(
    (state) => state.ListYearlevel_mapSlice
  );

  const ListYearLevel = Array.isArray(listYearlevel_maps?.data)
    ? listYearlevel_maps?.data?.map((listQuestions) => ({
      label:
        `${listQuestions?.yeargroup?.name}-${listQuestions?.region?.name}-${listQuestions?.region?.state}`!,
      value: listQuestions.id,
    }))
    : [];

  useEffect(() => {
    return () => {
      props.setInitialValues({});
    };
  }, [dispatch, props]);

  // Status list
  const CoursesSatus = [
    {
      label: "Active",
      value: true,
    },

    {
      label: "Inactive",
      value: false,
    },
  ];

  const imageFile: any = methods.watch("image_url");

  return (
    <FormProvider {...methods}>
      <form
        ref={props.formRef}
        onSubmit={methods.handleSubmit(onSubmit)}
        className="form_double_input"
      >
        <TextInputFieldC name="name" label="Name" type="text" />
        <Select
          name="yeargroup_country_map_id"
          label="Select  the Year group "
          options={ListYearLevel ?? []}
          value={
            ListYearLevel?.find(
              (list) => list.value === methods.getValues("year_group")
            ) ?? null
          }
          onChange={(_, value) => {
            const val = value as AutocompleteOption;
            methods.setValue("year_group", val?.value, {
              shouldValidate: true,
            });
          }}
        />

        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 3, // spacing between checkboxes
            mb: 2, // margin-bottom to separate from other fields
          }}
        >
          <FormControlLabel
            control={
              <Checkbox
                sx={{
                  marginLeft: "8px",
                  transform: "scale(1.5)", // Scale the size
                }}
                checked={methods.getValues("exam") === true}
                onChange={() => {
                  methods.setValue("exam", !methods.getValues("exam"), {
                    shouldValidate: true,
                  });
                }}
              />
            }
            label="Exam"
          />{" "}
          <FormControlLabel
            control={
              <Checkbox
                sx={{
                  marginLeft: "8px",
                  transform: "scale(1.5)", // Scale the size
                }}
                checked={methods.getValues("video") === true}
                onChange={() => {
                  methods.setValue("video", !methods.getValues("video"), {
                    shouldValidate: true,
                  });
                }}
              />
            }
            label="Video"
          />{" "}
          <FormControlLabel
            control={
              <Checkbox
                sx={{
                  marginLeft: "8px",
                  transform: "scale(1.5)", // Scale the size
                }}
                checked={methods.getValues("ai") === true}
                onChange={() => {
                  methods.setValue("ai", !methods.getValues("ai"), {
                    shouldValidate: true,
                  });
                }}
              />
            }
            label="AI"
          />{" "}
          <FormControlLabel
            control={
              <Checkbox
                sx={{
                  marginLeft: "8px",
                  transform: "scale(1.5)", // Scale the size
                }}
                checked={methods.getValues("booklet") === true}
                onChange={() => {
                  methods.setValue("booklet", !methods.getValues("booklet"), {
                    shouldValidate: true,
                  });
                }}
              />
            }
            label="Booklet"
          />{" "}
          <FormControlLabel
            control={
              <Checkbox
                sx={{
                  marginLeft: "8px",
                  transform: "scale(1.5)", // Scale the size
                }}
                checked={methods.getValues("study_note") === true}
                onChange={() => {
                  methods.setValue(
                    "study_note",
                    !methods.getValues("study_note"),
                    {
                      shouldValidate: true,
                    }
                  );
                }}
              />
            }
            label="Study Note"
          />
        </Box>
        <NumberInputField name="sequence" label="Sequence" type="number" />
        <NumberInputField
          name="duration"
          label="Duration in days"
          type="number"
          sx={{ width: "100%" }}
        />
        <NumberInputField name="cost" label="Cost" type="number" />
        <TextAreaInputField
          name="description"
          label="Description"
          type="text"
          minRows={1}
          maxRows={6}
        />
        <TextField
          fullWidth
          type="file"
          // size="small"
          label="Image upload"
          InputLabelProps={{ shrink: true }} // Correct way to shrink label
          onChange={(e: any) => {
            const file = e.target.files ? e.target.files[0] : null;

            methods.setValue("image_url", file, {
              shouldValidate: true,
            });
          }}
          error={!!methods.formState.errors.image_url}
          helperText={
            !!methods.formState.errors.image_url
              ? String(
                methods.formState.errors.image_url.message ===
                  "Input not instance of File"
                  ? "Please upload question image"
                  : methods.formState.errors.image_url.message
              )
              : ""
          }
        />
        <Select
          name="status"
          label="Status"
          options={CoursesSatus}
          value={
            CoursesSatus?.find(
              (role) => role.value === methods.getValues("status")
            ) ?? null
          }
          onChange={(_, value) => {
            const val = value as AutocompleteOption;
            methods.setValue("status", val?.value, { shouldValidate: true });
          }}
        />
        {imageFile && (
          <div>
            <span style={{ color: "#009DFF" }}>Preview for Question Image</span>
            <img
              src={
                typeof imageFile === "string"
                  ? imageFile
                  : URL.createObjectURL(imageFile)
              }
              alt="Uploaded preview"
              style={{ maxWidth: "100%", height: "auto" }}
            />
          </div>
        )}
      </form>
    </FormProvider>
  );
};

export default memo(CoursesForm);
